import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  Trash2,
  Settings2,
  X,
  MapPin,
  Briefcase,
  Plus,
  RotateCcw,
  CheckCircle2,
  CalendarCheck,
  Shield,
} from 'lucide-react';
import { useAuthedApi } from '../../lib/useAuthedApi';
import MultiSelect, { type MultiSelectOption } from '../../components/MultiSelect';
import Toggle from '../../components/Toggle';
import QrCodeButton from '../../components/QrCodeButton';
import { bookingUrl } from '../../lib/bookingUrl';

type Staff = {
  id: string;
  clerk_user_id: string;
  name: string;
  email: string;
  slug: string;
  timezone: string;
  is_active: number;
  // Comma-joined names of locations this person manages; null if not a manager.
  managed_location_names: string | null;
};

type Invite = {
  id: string;
  email: string;
  role: string;
  proposed_name: string | null;
  created_at: number;
};

type Location = {
  id: string;
  name: string;
  address: string;
  is_active: number;
};

type Service = {
  id: string;
  name: string;
  duration_minutes: number;
  is_active: number;
};

type StaffLocationRow = { id: string; is_default: number; is_manager: number };

type Assigning = {
  staffId: string;
  staffName: string;
  selectedLocationIds: Set<string>;
  defaultLocationId: string | null;
  managerLocationIds: Set<string>;
  selectedServiceIds: Set<string>;
  initialLocationIds: Set<string>;
  initialDefaultLocationId: string | null;
  initialManagerLocationIds: Set<string>;
  initialServiceIds: Set<string>;
} | null;

function formatRelativeTime(epochMs: number): string {
  const diff = Date.now() - epochMs;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(epochMs).toLocaleDateString();
}

export default function StaffAdmin() {
  const apiFn = useAuthedApi();
  const [staff, setStaff] = useState<Staff[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Staff id currently mid reactivate/delete, so its row buttons disable.
  const [inactiveBusy, setInactiveBusy] = useState<string | null>(null);
  // The current admin's own bookability. null = still loading/unknown.
  const [myBookable, setMyBookable] = useState<boolean | null>(null);
  const [myStaffId, setMyStaffId] = useState<string | null>(null);
  const [myName, setMyName] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [savingBookable, setSavingBookable] = useState(false);
  // Admin flipped the toggle on but hasn't confirmed a name yet: reveal the
  // name form without actually calling enable-booking until they submit.
  const [enabling, setEnabling] = useState(false);
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'manager'>('member');
  const [inviteLocs, setInviteLocs] = useState<Set<string>>(new Set());
  const [inviteDefaultLoc, setInviteDefaultLoc] = useState<string | null>(null);
  const [inviteSvcs, setInviteSvcs] = useState<Set<string>>(new Set());
  const [assigning, setAssigning] = useState<Assigning>(null);
  const [savingAssign, setSavingAssign] = useState(false);
  // Needed to build each staff member's personal booking URL for its QR code.
  const [orgSlug, setOrgSlug] = useState<string | null>(null);

  const load = async () => {
    try {
      const [s, i, locs, svcs, org] = await Promise.all([
        apiFn<Staff[]>('/staff'),
        apiFn<Invite[]>('/staff/invites'),
        apiFn<Location[]>('/locations'),
        apiFn<Service[]>('/services'),
        apiFn<{ slug: string }>('/organizations/me'),
      ]);
      setOrgSlug(org.slug);
      setStaff(s);
      setInvites(i);
      setLocations(locs.filter((l) => l.is_active === 1));
      setServices(svcs.filter((sv) => sv.is_active === 1));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // The admin's own bookability. 404 = not bookable (the default for admins).
  const loadMe = async () => {
    try {
      const me = await apiFn<{ id: string; name: string }>('/staff/me');
      setMyBookable(true);
      setMyStaffId(me.id);
      setMyName(me.name);
      setNameInput(me.name);
    } catch (e) {
      if ((e as { status?: number }).status === 404) {
        setMyBookable(false);
        setMyStaffId(null);
        setMyName('');
        setNameInput('');
      }
      // Other errors: leave null so the card stays hidden rather than misleading.
    }
  };

  useEffect(() => {
    void load();
    void loadMe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function enableBooking() {
    if (!nameInput.trim()) {
      setErr('Enter the name customers should see.');
      return;
    }
    setSavingBookable(true);
    setErr(null);
    setNotice(null);
    try {
      const row = await apiFn<{ name: string }>('/staff/me/enable-booking', {
        method: 'POST',
        body: { name: nameInput.trim() },
      });
      setNotice(`You're now bookable as ${row.name}. Set your availability and connect a calendar from your Dashboard.`);
      setEnabling(false);
      await Promise.all([loadMe(), load()]);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSavingBookable(false);
    }
  }

  async function saveMyBookingName() {
    if (!myStaffId || !nameInput.trim()) return;
    setSavingBookable(true);
    setErr(null);
    setNotice(null);
    try {
      await apiFn(`/staff/${myStaffId}`, {
        method: 'PATCH',
        body: { name: nameInput.trim() },
      });
      setNotice('Your booking name was updated.');
      await Promise.all([loadMe(), load()]);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSavingBookable(false);
    }
  }

  async function disableBooking() {
    if (
      !confirm(
        "Stop taking bookings? You'll be removed from the booking page but keep your admin access.",
      )
    ) {
      return;
    }
    setSavingBookable(true);
    setErr(null);
    setNotice(null);
    try {
      await apiFn('/staff/me/disable-booking', { method: 'POST' });
      setNotice("You're no longer bookable.");
      setEnabling(false);
      await Promise.all([loadMe(), load()]);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSavingBookable(false);
    }
  }

  async function sendInvite() {
    const trimmed = email.trim();
    if (!trimmed) {
      setErr('Email is required.');
      return;
    }
    if (inviteRole === 'manager' && inviteLocs.size === 0) {
      setErr('A manager needs at least one location to manage — pick one below.');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await apiFn('/staff/invite', {
        body: {
          email: trimmed,
          role: inviteRole,
          location_ids: [...inviteLocs],
          default_location_id:
            inviteDefaultLoc && inviteLocs.has(inviteDefaultLoc) ? inviteDefaultLoc : null,
          service_type_ids: [...inviteSvcs],
        },
      });
      setEmail('');
      setInviteRole('member');
      setInviteLocs(new Set());
      setInviteDefaultLoc(null);
      setInviteSvcs(new Set());
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  // Keeps default-location consistent with the set of selected locations:
  // clears default when its location is removed; auto-picks the first location
  // when nothing was selected before. Same semantics for the per-staff assign
  // flow below.
  function onInviteLocsChange(next: Set<string>) {
    let nextDefault = inviteDefaultLoc;
    if (nextDefault && !next.has(nextDefault)) nextDefault = null;
    if (next.size >= 1 && nextDefault === null) {
      nextDefault = next.values().next().value ?? null;
    }
    setInviteLocs(next);
    setInviteDefaultLoc(nextDefault);
  }

  async function revokeInvite(id: string, recipientEmail: string) {
    if (!confirm(`Revoke the invitation to ${recipientEmail}?`)) return;
    try {
      await apiFn(`/staff/invites/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  // Maps the staff-removal guard error codes to admin-facing copy.
  function removalErrMsg(e: unknown, name: string): string {
    const body = (e as { body?: { error?: string; count?: number } }).body;
    switch (body?.error) {
      case 'cannot_target_self':
        return "You can't deactivate or delete your own admin account.";
      case 'last_admin':
        return `${name} is the only admin on this account. Make someone else an admin (in your account menu) before removing them.`;
      case 'has_bookings':
        return `${name} has ${body.count ?? 'existing'} booking(s) on record, so they can't be permanently deleted. They'll stay deactivated instead.`;
      case 'must_deactivate_first':
        return `Deactivate ${name} before deleting them permanently.`;
      default:
        return (e as Error).message;
    }
  }

  async function deactivate(id: string, name: string) {
    if (!confirm(`Deactivate ${name}? They'll stop appearing on your booking page.`)) return;
    setErr(null);
    setNotice(null);
    try {
      await apiFn(`/staff/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setErr(removalErrMsg(e, name));
    }
  }

  async function reactivate(id: string, name: string) {
    setErr(null);
    setNotice(null);
    setInactiveBusy(id);
    try {
      const r = await apiFn<{ status: string; email?: string }>(
        `/staff/${id}/reactivate`,
        { method: 'POST' },
      );
      if (r.status === 'reinvited') {
        setNotice(
          `${name} is no longer in your organization, so we re-sent an invite to ${r.email ?? name}. They'll be reactivated once they accept.`,
        );
      } else {
        setNotice(`${name} reactivated.`);
      }
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setInactiveBusy(null);
    }
  }

  async function deletePermanent(id: string, name: string) {
    if (
      !confirm(
        `Permanently delete ${name}? This can't be undone — their locations, services, availability, and calendar connections are removed.`,
      )
    ) {
      return;
    }
    setErr(null);
    setNotice(null);
    setInactiveBusy(id);
    try {
      await apiFn(`/staff/${id}/permanent`, { method: 'DELETE' });
      setNotice(`${name} permanently deleted.`);
      await load();
    } catch (e) {
      setErr(removalErrMsg(e, name));
    } finally {
      setInactiveBusy(null);
    }
  }

  async function startAssign(s: Staff) {
    setErr(null);
    try {
      const [locRows, svcRows] = await Promise.all([
        apiFn<StaffLocationRow[]>(`/staff/${s.id}/locations`),
        apiFn<Array<{ id: string }>>(`/staff/${s.id}/services`),
      ]);
      const locIds = new Set(locRows.map((r) => r.id));
      const defaultLoc = locRows.find((r) => r.is_default === 1)?.id ?? null;
      const managerIds = new Set(locRows.filter((r) => r.is_manager === 1).map((r) => r.id));
      const svcIds = new Set(svcRows.map((r) => r.id));
      setAssigning({
        staffId: s.id,
        staffName: s.name,
        selectedLocationIds: new Set(locIds),
        defaultLocationId: defaultLoc,
        managerLocationIds: new Set(managerIds),
        selectedServiceIds: new Set(svcIds),
        initialLocationIds: new Set(locIds),
        initialDefaultLocationId: defaultLoc,
        initialManagerLocationIds: new Set(managerIds),
        initialServiceIds: new Set(svcIds),
      });
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  function cancelAssign() {
    setAssigning(null);
  }

  function onAssignLocsChange(next: Set<string>) {
    if (!assigning) return;
    let nextDefault = assigning.defaultLocationId;
    if (nextDefault && !next.has(nextDefault)) nextDefault = null;
    if (next.size >= 1 && nextDefault === null) {
      nextDefault = next.values().next().value ?? null;
    }
    // Drop manager flags for any location that was unassigned.
    const nextManagers = new Set([...assigning.managerLocationIds].filter((id) => next.has(id)));
    setAssigning({
      ...assigning,
      selectedLocationIds: next,
      defaultLocationId: nextDefault,
      managerLocationIds: nextManagers,
    });
  }

  function setDefault(id: string) {
    if (!assigning) return;
    if (!assigning.selectedLocationIds.has(id)) return;
    setAssigning({ ...assigning, defaultLocationId: id });
  }

  function onManagerLocsChange(next: Set<string>) {
    if (!assigning) return;
    // Only locations they're assigned to can be managed (the options are
    // already limited to those, but guard anyway).
    const scoped = new Set([...next].filter((id) => assigning.selectedLocationIds.has(id)));
    setAssigning({ ...assigning, managerLocationIds: scoped });
  }

  function onAssignSvcsChange(next: Set<string>) {
    if (!assigning) return;
    setAssigning({ ...assigning, selectedServiceIds: next });
  }

  async function saveAssign() {
    if (!assigning) return;
    setSavingAssign(true);
    setErr(null);
    try {
      const locSet = assigning.selectedLocationIds;
      const initLoc = assigning.initialLocationIds;
      const mgr = assigning.managerLocationIds;
      const initMgr = assigning.initialManagerLocationIds;
      const locsChanged =
        locSet.size !== initLoc.size ||
        [...locSet].some((x) => !initLoc.has(x)) ||
        assigning.defaultLocationId !== assigning.initialDefaultLocationId ||
        mgr.size !== initMgr.size ||
        [...mgr].some((x) => !initMgr.has(x));

      if (locsChanged) {
        await apiFn(`/staff/${assigning.staffId}/locations`, {
          method: 'PUT',
          body: {
            assignments: [...locSet].map((id) => ({
              location_id: id,
              is_default: id === assigning.defaultLocationId,
              is_manager: mgr.has(id),
            })),
          },
        });
      }

      const svcSet = assigning.selectedServiceIds;
      const initSvc = assigning.initialServiceIds;
      const svcChanged =
        svcSet.size !== initSvc.size || [...svcSet].some((x) => !initSvc.has(x));
      if (svcChanged) {
        await apiFn(`/staff/${assigning.staffId}/services`, {
          method: 'PUT',
          body: { service_type_ids: [...svcSet] },
        });
      }

      setAssigning(null);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSavingAssign(false);
    }
  }

  const activeStaff = useMemo(() => staff.filter((s) => s.is_active === 1), [staff]);
  const inactiveStaff = useMemo(() => staff.filter((s) => s.is_active === 0), [staff]);

  const locationOptions = useMemo<MultiSelectOption[]>(
    () => locations.map((l) => ({ id: l.id, label: l.name, sublabel: l.address })),
    [locations],
  );
  const serviceOptions = useMemo<MultiSelectOption[]>(
    () =>
      services.map((sv) => ({
        id: sv.id,
        label: sv.name,
        sublabel: `${sv.duration_minutes} min`,
      })),
    [services],
  );

  return (
    <div>
      {notice && (
        <div className="banner banner-ok" style={{ marginBottom: '1rem' }}>
          <CheckCircle2 size={16} />
          <span>{notice}</span>
          <button
            className="ghost banner-dismiss"
            onClick={() => setNotice(null)}
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {myBookable !== null && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>
            <CalendarCheck size={18} /> Take bookings yourself
          </h2>

          <Toggle
            checked={myBookable || enabling}
            disabled={savingBookable}
            onChange={(next) => {
              if (next) {
                // Reveal the name form; only actually enable once they submit.
                setEnabling(true);
              } else if (myBookable) {
                void disableBooking();
              } else {
                setEnabling(false);
              }
            }}
            label="Appear as a bookable staff member"
            description="Admins aren't bookable by default. Turn this on if you also meet with customers."
          />

          {/* Fields stay hidden until the toggle is on, so the section doesn't
              overwhelm admins who don't take bookings. */}
          {myBookable ? (
            <>
              <p className="muted">
                Customers see this name on your booking page &mdash; make sure
                it&rsquo;s your name, not the company&rsquo;s.
              </p>
              <label>
                <span>Your booking name</span>
                <input
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="e.g. Sarah Lee"
                  maxLength={120}
                />
              </label>
              <button
                className="primary"
                onClick={saveMyBookingName}
                disabled={savingBookable || !nameInput.trim() || nameInput.trim() === myName}
                style={{ marginTop: '0.5rem' }}
              >
                {savingBookable ? 'Saving…' : 'Save name'}
              </button>
              <p className="field-help">
                Set your availability and connect a calendar from your Dashboard.
              </p>
            </>
          ) : (
            enabling && (
              <>
                <label>
                  <span>Your booking name</span>
                  <input
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="e.g. Sarah Lee"
                    maxLength={120}
                    autoFocus
                  />
                </label>
                <button
                  className="primary"
                  onClick={enableBooking}
                  disabled={savingBookable || !nameInput.trim()}
                  style={{ marginTop: '0.5rem' }}
                >
                  {savingBookable ? 'Saving…' : 'Start taking bookings'}
                </button>
              </>
            )
          )}
        </div>
      )}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Add to your team</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Send an invite. They&rsquo;ll get a link to set up their account and
          connect a calendar. Pre-pick locations and services so they&rsquo;re
          bookable the moment they accept &mdash; tweak anytime.
        </p>

        <label>
          <span>Email</span>
          <input
            type="email"
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !submitting && email.trim()) {
                void sendInvite();
              }
            }}
          />
        </label>

        <label>
          <span>Role</span>
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as 'member' | 'manager')}
          >
            <option value="member">Member — books their own appointments</option>
            <option value="manager">Manager — runs the location(s) below</option>
          </select>
          <p className="field-help">
            {inviteRole === 'manager'
              ? 'A manager sees and manages every appointment at their location(s) and can promote other members to manager. Pick their location(s) below.'
              : 'A member sees and manages only their own appointments.'}
          </p>
        </label>

        <div className="assign-grid">
          <div>
            <h3>
              <MapPin size={14} /> Locations
            </h3>
            <MultiSelect
              options={locationOptions}
              selectedIds={inviteLocs}
              onChange={onInviteLocsChange}
              placeholder="Pick locations…"
              searchPlaceholder="Search locations…"
              emptyOptionsMessage={
                <span>
                  No active locations yet.{' '}
                  <Link to="/admin/locations" className="inline-action">
                    <Plus size={12} /> Add a location
                  </Link>
                </span>
              }
            />
            {inviteLocs.size >= 2 && (
              <label style={{ marginTop: '0.6rem' }}>
                <span className="muted small">Default location</span>
                <select
                  value={inviteDefaultLoc ?? ''}
                  onChange={(e) => setInviteDefaultLoc(e.target.value || null)}
                >
                  {locations
                    .filter((l) => inviteLocs.has(l.id))
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                </select>
              </label>
            )}
          </div>

          <div>
            <h3>
              <Briefcase size={14} /> Services
            </h3>
            <MultiSelect
              options={serviceOptions}
              selectedIds={inviteSvcs}
              onChange={setInviteSvcs}
              placeholder="Pick services…"
              searchPlaceholder="Search services…"
              emptyOptionsMessage={
                <span>
                  No active services yet.{' '}
                  <Link to="/admin/services" className="inline-action">
                    <Plus size={12} /> Add a service
                  </Link>
                </span>
              }
            />
          </div>
        </div>

        {err && !assigning && <p className="err" style={{ marginTop: '0.5rem' }}>{err}</p>}

        <button
          className="primary"
          onClick={sendInvite}
          disabled={submitting || !email.trim()}
          style={{ marginTop: '0.5rem' }}
        >
          <Mail size={16} /> {submitting ? 'Sending…' : 'Send invite'}
        </button>
      </div>

      {invites.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Pending invitations</h3>
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Sent</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.email}</td>
                  <td className="muted">{formatRelativeTime(inv.created_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="ghost"
                      onClick={() => revokeInvite(inv.id, inv.email)}
                      title="Revoke invitation"
                    >
                      <Trash2 size={14} /> Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>
          Your team {activeStaff.length > 0 && (
            <span className="muted small">({activeStaff.length})</span>
          )}
        </h3>

        {loading ? (
          <p className="muted">Loading…</p>
        ) : activeStaff.length === 0 ? (
          <p className="muted">
            No active team members yet. Invite someone above to get started.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Booking link</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {activeStaff.map((s) => {
                const isAssigningThis = assigning?.staffId === s.id;
                if (isAssigningThis && assigning) {
                  return (
                    <tr key={s.id}>
                      <td colSpan={4} style={{ padding: '1rem' }}>
                        <div className="row" style={{ marginBottom: '0.5rem' }}>
                          <strong>Assignments for {assigning.staffName}</strong>
                          <span className="spacer" />
                          <button className="ghost" onClick={cancelAssign} aria-label="Cancel">
                            <X size={16} />
                          </button>
                        </div>

                        <div className="assign-grid">
                          <div>
                            <h3>
                              <MapPin size={14} /> Locations
                            </h3>
                            <p className="field-help" style={{ marginTop: 0 }}>
                              Pick where they work. One can be the default
                              (used when a bookee follows their personal link).
                            </p>
                            <MultiSelect
                              options={locationOptions}
                              selectedIds={assigning.selectedLocationIds}
                              onChange={onAssignLocsChange}
                              placeholder="Pick locations…"
                              searchPlaceholder="Search locations…"
                              emptyOptionsMessage={
                                <span>
                                  No active locations yet.{' '}
                                  <Link to="/admin/locations" className="inline-action">
                                    <Plus size={12} /> Add a location
                                  </Link>
                                </span>
                              }
                            />
                            {assigning.selectedLocationIds.size >= 2 && (
                              <label style={{ marginTop: '0.6rem' }}>
                                <span className="muted small">Default location</span>
                                <select
                                  value={assigning.defaultLocationId ?? ''}
                                  onChange={(e) => setDefault(e.target.value)}
                                >
                                  {locations
                                    .filter((l) => assigning.selectedLocationIds.has(l.id))
                                    .map((l) => (
                                      <option key={l.id} value={l.id}>
                                        {l.name}
                                      </option>
                                    ))}
                                </select>
                              </label>
                            )}
                            {assigning.selectedLocationIds.size >= 1 && (
                              <div style={{ marginTop: '0.75rem' }}>
                                <h3>
                                  <Shield size={14} /> Location Manager of…
                                </h3>
                                <p className="field-help" style={{ marginTop: 0 }}>
                                  A manager sees and manages every appointment at the
                                  location, and can promote other members to manager.
                                </p>
                                <MultiSelect
                                  options={locationOptions.filter((o) =>
                                    assigning.selectedLocationIds.has(o.id),
                                  )}
                                  selectedIds={assigning.managerLocationIds}
                                  onChange={onManagerLocsChange}
                                  placeholder="Pick locations they manage…"
                                  searchPlaceholder="Search locations…"
                                />
                              </div>
                            )}
                          </div>

                          <div>
                            <h3>
                              <Briefcase size={14} /> Services
                            </h3>
                            <p className="field-help" style={{ marginTop: 0 }}>
                              Pick what they offer. A bookee will only see
                              services this person can do <em>and</em> the
                              location supports.
                            </p>
                            <MultiSelect
                              options={serviceOptions}
                              selectedIds={assigning.selectedServiceIds}
                              onChange={onAssignSvcsChange}
                              placeholder="Pick services…"
                              searchPlaceholder="Search services…"
                              emptyOptionsMessage={
                                <span>
                                  No active services yet.{' '}
                                  <Link to="/admin/services" className="inline-action">
                                    <Plus size={12} /> Add a service
                                  </Link>
                                </span>
                              }
                            />
                          </div>
                        </div>

                        {err && <p className="err">{err}</p>}
                        <div className="row" style={{ gap: '0.5rem' }}>
                          <button
                            className="primary"
                            onClick={saveAssign}
                            disabled={savingAssign}
                          >
                            {savingAssign ? 'Saving…' : 'Save assignments'}
                          </button>
                          <button className="ghost" onClick={cancelAssign}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={s.id}>
                    <td>
                      <span className="row" style={{ gap: '0.4rem', alignItems: 'center' }}>
                        {s.name}
                        {s.managed_location_names && (
                          <span
                            className="badge badge-ok"
                            title={`Manager of ${s.managed_location_names}`}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                          >
                            <Shield size={12} /> Manager
                          </span>
                        )}
                      </span>
                    </td>
                    <td>{s.email}</td>
                    <td className="muted small">/staff/{s.slug}</td>
                    <td style={{ textAlign: 'right' }}>
                      {orgSlug && (
                        <QrCodeButton
                          className="ghost"
                          url={bookingUrl(orgSlug, `/staff/${s.slug}`)}
                          title={s.name}
                          filename={`${orgSlug}-${s.slug}`}
                          note="This link only works once they've connected a calendar from their Dashboard."
                        />
                      )}
                      <button
                        className="ghost"
                        onClick={() => startAssign(s)}
                        title="Assign locations & services"
                      >
                        <Settings2 size={14} /> Assign
                      </button>
                      <button
                        className="ghost"
                        onClick={() => deactivate(s.id, s.name)}
                        title="Deactivate"
                        style={{ marginLeft: '0.25rem' }}
                      >
                        <Trash2 size={14} /> Deactivate
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {inactiveStaff.length > 0 && (
        <details className="dev-settings">
          <summary>Inactive team members ({inactiveStaff.length})</summary>
          <div className="dev-settings-body">
            <p className="muted" style={{ marginTop: 0 }}>
              These profiles are deactivated &mdash; someone turned their access
              off, or they were removed from the organization (which deactivates
              them automatically). They don&rsquo;t appear on your booking page.
              <strong> Reactivate</strong> restores access instantly, or re-sends
              an invite if they&rsquo;ve left the org. <strong>Delete</strong>
              {' '}removes them for good (blocked if they have bookings on record).
            </p>
            {err && <p className="err">{err}</p>}
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Booking link</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {inactiveStaff.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{s.email}</td>
                    <td className="muted small">/staff/{s.slug}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        className="ghost"
                        onClick={() => reactivate(s.id, s.name)}
                        disabled={inactiveBusy === s.id}
                        title="Reactivate or re-invite"
                      >
                        <RotateCcw size={14} /> Reactivate
                      </button>
                      <button
                        className="ghost"
                        onClick={() => deletePermanent(s.id, s.name)}
                        disabled={inactiveBusy === s.id}
                        title="Permanently delete"
                        style={{ marginLeft: '0.25rem' }}
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}
